import { useState, useEffect, useMemo, useCallback } from 'react';
import { Outlet, useLoaderData, useLocation } from 'react-router-dom';
import { AppNav } from '@/components/app/AppNav';
import { AudioPlaybar } from '@/components/audio/AudioPlaybar';
import type { ProtectedLoaderData } from '../lib/loaders';
import {
  useAudioSessionSync,
  loadAudioState,
  saveAudioState,
} from '@/hooks/useAudioSessionSync';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { setSentryUser, clearSentryUser, setSentryContext } from '@/lib/sentry';
import { useMusicLibrary, type Track } from '@/hooks/useMusicLibrary';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { useHiddenTracks } from '@/hooks/useHiddenTracks';
import { useSelectedAlbum } from '@/hooks/useSelectedAlbum';
import { useFavoriteTracks } from '@/hooks/useFavoriteTracks';
import { getAlbumCoverPath, getTrackPath } from '@/lib/audioPathUtils';

export type MusicContextType = {
  selectedAlbumId: string | null;
  setSelectedAlbumId: (id: string | null) => void;
  albums: ReturnType<typeof useMusicLibrary>['albums'];
  tracks: ReturnType<typeof useMusicLibrary>['tracks'];
  handleAlbumSelect: (albumId: string) => void;
};

export type AudioControlType = {
  isAudioPlaying: boolean;
  setIsAudioPlaying: (playing: boolean) => void;
  currentTrackIndex: number;
  handleNextTrack: () => void;
  handlePreviousTrack: () => void;
  handleTrackComplete: () => void;
};

export function ProtectedLayout() {
  const { session } = useLoaderData() as ProtectedLoaderData;
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [albumCount, setAlbumCount] = useState(1); // 1, 2, or 3 albums based on duration

  const isOnboarding = location.pathname.includes('/onboarding');

  // Listen for focus duration changes from DashboardPage
  useEffect(() => {
    const handleDurationChange = (event: CustomEvent<{ duration: number; albumCount: number }>) => {
      setAlbumCount(event.detail.albumCount);
      setCurrentTrackIndex(0); // Reset to first track when duration changes
    };

    window.addEventListener('focusDurationChanged', handleDurationChange as EventListener);
    return () => {
      window.removeEventListener('focusDurationChanged', handleDurationChange as EventListener);
    };
  }, []);

  useAudioSessionSync();

  const { albums, tracks, libraryError } = useMusicLibrary();
  const { isPro, audioQuality } = useUserPreferences(session.user.id);
  const { hiddenTrackIds, hideTrack: hideTrackFn } = useHiddenTracks(
    session.user.id
  );
  const { selectedAlbumId, setSelectedAlbumId, handleAlbumSelect } =
    useSelectedAlbum(session.user.id);
  const favoriteTracksWithCounts = useFavoriteTracks(
    session.user.id,
    selectedAlbumId,
    tracks,
    hiddenTrackIds
  );

  useEffect(() => {
    if (session?.user) {
      setSentryUser(session.user.id, session.user.email);
      if (isPro) {
        setSentryContext('subscription', { tier: 'pro' });
      }
    } else {
      clearSentryUser();
    }
    return () => clearSentryUser();
  }, [session, isPro]);

  const albumTracks = useMemo(() => {
    if (selectedAlbumId === 'favorites') {
      return favoriteTracksWithCounts;
    }

    // Get tracks for the primary album
    const primaryTracks = tracks.filter(
      (t: Track) =>
        t.albumId === selectedAlbumId && !hiddenTrackIds.includes(t.trackId)
    );

    // If only 1 album (25min), return just the primary album tracks
    if (albumCount === 1) {
      return primaryTracks;
    }

    // For extended sessions (50min=2 albums, 75min=3 albums), chain additional albums
    // Find albums with similar behavior context for a cohesive listening experience
    const selectedAlbum = albums.find(a => a.albumId === selectedAlbumId);
    if (!selectedAlbum) {
      return primaryTracks;
    }

    // Get other albums (excluding selected and favorites)
    const otherAlbums = albums.filter(a =>
      a.albumId !== selectedAlbumId && a.albumId !== 'favorites'
    );

    // Try to match by behavior context first, otherwise just use next available albums
    const primaryContext = selectedAlbum.behaviorContext?.[0]?.toLowerCase() || '';
    const matchingAlbums = otherAlbums.filter(a =>
      a.behaviorContext?.some(ctx => ctx.toLowerCase().includes(primaryContext.split(' ')[0]))
    );

    // Use matching albums if available, otherwise use any albums
    const albumsToChain = matchingAlbums.length > 0 ? matchingAlbums : otherAlbums;

    // Build the combined track list
    let combinedTracks = [...primaryTracks];

    // Add tracks from additional albums (up to albumCount - 1 more albums)
    for (let i = 0; i < albumCount - 1 && i < albumsToChain.length; i++) {
      const additionalAlbum = albumsToChain[i];
      const additionalTracks = tracks.filter(
        (t: Track) =>
          t.albumId === additionalAlbum.albumId && !hiddenTrackIds.includes(t.trackId)
      );
      combinedTracks = [...combinedTracks, ...additionalTracks];
    }

    return combinedTracks;
  }, [tracks, selectedAlbumId, hiddenTrackIds, favoriteTracksWithCounts, albumCount, albums]);

  useEffect(() => {
    setCurrentTrackIndex(0);
  }, [selectedAlbumId]);

  useEffect(() => {
    if (albums.length > 0 && selectedAlbumId) {
      const savedTrackIndex = loadAudioState();
      if (savedTrackIndex !== null && savedTrackIndex < albumTracks.length) {
        setCurrentTrackIndex(savedTrackIndex);
      }
    }
  }, [albums.length, selectedAlbumId, albumTracks.length]);

  useEffect(() => {
    if (currentTrackIndex >= 0) {
      saveAudioState(currentTrackIndex);
    }
  }, [currentTrackIndex]);

  const handleNextTrack = useCallback(() => {
    if (albumTracks.length > 0) {
      setCurrentTrackIndex((currentTrackIndex + 1) % albumTracks.length);
    }
  }, [albumTracks.length, currentTrackIndex]);

  const handlePreviousTrack = useCallback(() => {
    if (albumTracks.length > 0) {
      setCurrentTrackIndex(
        currentTrackIndex === 0 ? albumTracks.length - 1 : currentTrackIndex - 1
      );
    }
  }, [albumTracks.length, currentTrackIndex]);

  const handleTrackComplete = useCallback(() => {
    if (albumTracks.length > 0) {
      setCurrentTrackIndex((currentTrackIndex + 1) % albumTracks.length);
    }
  }, [albumTracks.length, currentTrackIndex]);

  const handleHideTrack = useCallback(
    async (trackId: string) => {
      const success = await hideTrackFn(trackId);
      if (success && albumTracks[currentTrackIndex]?.trackId === trackId) {
        handleNextTrack();
      }
    },
    [hideTrackFn, albumTracks, currentTrackIndex, handleNextTrack]
  );

  useKeyboardShortcuts({
    onPlayPause: () => setIsAudioPlaying(prev => !prev),
    onNextTrack: handleNextTrack,
    onPreviousTrack: handlePreviousTrack,
    enabled: !isOnboarding,
  });

  const musicContext: MusicContextType = {
    selectedAlbumId,
    setSelectedAlbumId,
    albums,
    tracks,
    handleAlbumSelect,
  };

  const audioControl: AudioControlType = {
    isAudioPlaying,
    setIsAudioPlaying,
    currentTrackIndex,
    handleNextTrack,
    handlePreviousTrack,
    handleTrackComplete,
  };

  const selectedAlbum = useMemo(() => {
    if (selectedAlbumId === 'favorites') {
      return {
        type: 'album' as const,
        albumId: 'favorites',
        slug: 'favorites',
        title: 'My Favorites',
        behaviorContext: ['favorites'],
        soundMode: ['all'],
      };
    }
    return albums.find(
      (a: ReturnType<typeof useMusicLibrary>['albums'][number]) =>
        a.albumId === selectedAlbumId
    );
  }, [albums, selectedAlbumId]);

  const currentTrack = useMemo(
    () => albumTracks[currentTrackIndex],
    [albumTracks, currentTrackIndex]
  );

  const albumCover = useMemo(
    () => (selectedAlbum ? getAlbumCoverPath(selectedAlbum.slug) : ''),
    [selectedAlbum]
  );

  const trackPath = useMemo(
    () => getTrackPath(currentTrack, selectedAlbum, isPro, audioQuality),
    [currentTrack, selectedAlbum, isPro, audioQuality]
  );

  return (
    <div className="relative min-h-screen bg-bg-primary">
      <AppNav
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {libraryError && (
        <div className="fixed top-[90px] md:top-[100px] lg:top-[120px] left-0 right-0 z-50 bg-error-pink/10 border-b border-error-pink/30 px-4 py-3">
          <div className="max-w-[1728px] mx-auto flex items-center justify-between gap-4">
            <p className="font-sans text-sm text-error-pink">{libraryError}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-1.5 text-sm font-sans text-error-pink border border-error-pink/30 rounded-lg hover:bg-error-pink/10 transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      )}

      <main className="relative pt-[90px] md:pt-[100px] lg:pt-[120px] px-4 md:px-6 lg:px-8 z-10">
        <Outlet
          context={{ session, musicContext, audioControl, setMobileMenuOpen }}
        />
      </main>

      <AudioPlaybar
        albumId={selectedAlbumId}
        albumTitle={selectedAlbum?.title || 'No album selected'}
        albumCover={albumCover}
        trackPath={trackPath}
        trackTitle={currentTrack?.title}
        trackId={currentTrack?.trackId}
        userId={session.user.id}
        isTimerPlaying={isAudioPlaying}
        onPlayPause={setIsAudioPlaying}
        onNextTrack={handleNextTrack}
        onPreviousTrack={handlePreviousTrack}
        onTrackComplete={handleTrackComplete}
        onLoadError={handleNextTrack}
        onOpenMenu={() => setMobileMenuOpen(true)}
        onHideTrack={handleHideTrack}
      />
    </div>
  );
}
